if (!customElements.get('pinch-zoom-enabler')) {
  customElements.define(
    'pinch-zoom-enabler',
    class PinchZoomEnabler extends HTMLElement {
      constructor() {
        super();
        this.swiperInstance = null;
        this.isPinching = false;
        this.isProcessing = false; // Prevent rapid-fire events
        this.lastGestureTime = 0; // Track last gesture
        this.reenableTimeout = null; // Track timeout
      }

      connectedCallback() {
        // Wait for Swiper to initialize
        this.waitForSwiper();
      }

      waitForSwiper() {
        let attempts = 0;
        const maxAttempts = 20;
        
        const checkSwiper = () => {
          // Swiper stores instance on the DOM element
          if (this.swiper) {
            this.swiperInstance = this.swiper;
            this.setupEventListeners();
            return;
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkSwiper, 100);
          } else {
            // Swiper not found, still setup listeners
            this.setupEventListeners();
          }
        };
        
        checkSwiper();
      }

      setupEventListeners() {
        // Touch events to detect pinch - must be non-passive to work
        this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
        this.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
        this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });
        this.addEventListener('touchcancel', this.handleTouchEnd.bind(this), { passive: false });
      }

      handleTouchStart(e) {
        // Detect 2-finger pinch gesture
        if (e.touches.length === 2) {
          // Throttle rapid gestures - prevent crash from too many events
          const now = Date.now();
          if (now - this.lastGestureTime < 100) {
            return; // Ignore if less than 100ms since last gesture
          }
          this.lastGestureTime = now;
          
          // Prevent processing if already handling a gesture
          if (this.isProcessing) return;
          
          this.isPinching = true;
          this.isProcessing = true;
          
          // Clear any pending re-enable timeout
          if (this.reenableTimeout) {
            clearTimeout(this.reenableTimeout);
            this.reenableTimeout = null;
          }
          
          // Disable Swiper immediately to allow native browser zoom
          if (this.swiperInstance && this.swiperInstance.enabled) {
            try {
              this.swiperInstance.disable();
            } catch (error) {
              // Silently handle any errors during disable
            }
          }
        }
      }

      handleTouchMove(e) {
        // If pinching, do nothing - let browser handle it
        if (e.touches.length === 2 && this.isPinching) {
          // Don't prevent default - allow browser zoom
          return;
        }
      }

      handleTouchEnd(e) {
        // Re-enable Swiper when all fingers are lifted
        if (e.touches.length === 0 && this.isPinching) {
          this.isPinching = false;
          
          // Clear any existing timeout
          if (this.reenableTimeout) {
            clearTimeout(this.reenableTimeout);
          }
          
          if (this.swiperInstance) {
            // Delay to ensure zoom gesture completes
            this.reenableTimeout = setTimeout(() => {
              try {
                if (this.swiperInstance && !this.swiperInstance.enabled) {
                  this.swiperInstance.enable();
                }
              } catch (error) {
                // Silently handle any errors during enable
              } finally {
                // Reset processing flag
                this.isProcessing = false;
                this.reenableTimeout = null;
              }
            }, 300); // Increased delay for stability
          } else {
            this.isProcessing = false;
          }
        }
      }

      disconnectedCallback() {
        // Clear any pending timeouts
        if (this.reenableTimeout) {
          clearTimeout(this.reenableTimeout);
          this.reenableTimeout = null;
        }
        
        // Clean up event listeners
        this.removeEventListener('touchstart', this.handleTouchStart);
        this.removeEventListener('touchmove', this.handleTouchMove);
        this.removeEventListener('touchend', this.handleTouchEnd);
        this.removeEventListener('touchcancel', this.handleTouchEnd);
        
        // Re-enable Swiper if it was disabled
        if (this.swiperInstance && !this.swiperInstance.enabled) {
          try {
            this.swiperInstance.enable();
          } catch (error) {
            // Silently handle cleanup errors
          }
        }
      }
    }
  );
}

